/* The French-programme intake questions, and the tree that relates them.

   Mirrors backend/french_intake.py: the same five fields, the same codes, the
   same dependencies. The server is authoritative — it re-derives the whole
   block on every write — so this exists to make the form ask the right
   questions rather than to decide anything.

   Codes are canonical and labels are produced only for display, as everywhere
   else in the person view model. */

export const FRENCH_GOAL_OPTIONS = [
  { value: 'work_permit', label: 'Work permit' },
  { value: 'tcf_test', label: 'Taking the TCF test' },
  { value: 'tef_test', label: 'Taking the TEF test' },
  { value: 'work_requirement', label: 'For work (work requirement)' },
  { value: 'self_improvement', label: 'Self-improvement' },
];

// The goals that end in an exam, and so the only ones with a target level.
export const FRENCH_TEST_GOALS = ['tcf_test', 'tef_test'];

export const FRENCH_TARGET_LEVEL_OPTIONS = [
  { value: 'b1', label: 'B1' },
  { value: 'b2', label: 'B2' },
  { value: 'c1_plus', label: 'C1 or above' },
];

// `none` is "I don't know French" — not a CEFR level, which is why the
// questions below it are not asked of anyone who picks it.
export const FRENCH_CURRENT_LEVEL_OPTIONS = [
  { value: 'none', label: "I don't know French" },
  { value: 'a1', label: 'A1' },
  { value: 'a2', label: 'A2' },
  { value: 'b1', label: 'B1' },
  { value: 'b2_plus', label: 'B2 or above' },
];
export const FRENCH_NO_FRENCH = 'none';

export const FRENCH_TEST_PROOF_OPTIONS = [
  { value: 'yes', label: 'Yes — result provided' },
  { value: 'no', label: 'No' },
];

export const FRENCH_PLACEMENT_OPTIONS = [
  { value: 'start_beginning', label: 'Start at the beginning of the programme' },
  { value: 'placement_test', label: 'Take a placement test instead' },
];

// In the order the questions are asked.
export const FRENCH_FIELDS = [
  'frenchGoal',
  'frenchTargetLevel',
  'frenchCurrentLevel',
  'frenchTestProof',
  'frenchPlacementPreference',
];

const OPTIONS_BY_FIELD = {
  frenchGoal: FRENCH_GOAL_OPTIONS,
  frenchTargetLevel: FRENCH_TARGET_LEVEL_OPTIONS,
  frenchCurrentLevel: FRENCH_CURRENT_LEVEL_OPTIONS,
  frenchTestProof: FRENCH_TEST_PROOF_OPTIONS,
  frenchPlacementPreference: FRENCH_PLACEMENT_OPTIONS,
};

/* The label for one stored answer. An unknown code shows as itself: the server
   only ever stores a known one, so a surprise is worth seeing. */
export function frenchLabel(field, value) {
  const code = String(value || '').trim();
  if (!code) return '';
  const match = (OPTIONS_BY_FIELD[field] || []).find((option) => option.value === code);
  return match ? match.label : code;
}

/* Which of the five questions this set of answers actually asks.

   The same tree as `normalize_answers` on the server, read forwards: a target
   level only when the goal is an exam, a test-result question only for someone
   who knows some French, and a starting point only when they have no result. */
export function frenchQuestionsAsked(answers) {
  const view = answers || {};
  const goal = String(view.frenchGoal || '').trim();
  const level = String(view.frenchCurrentLevel || '').trim();
  const proof = String(view.frenchTestProof || '').trim();
  const asksProof = Boolean(level) && level !== FRENCH_NO_FRENCH;
  return {
    frenchGoal: true,
    frenchTargetLevel: FRENCH_TEST_GOALS.includes(goal),
    frenchCurrentLevel: true,
    frenchTestProof: asksProof,
    frenchPlacementPreference: asksProof && proof === 'no',
  };
}

/* Whether this person was ever asked these questions — what tells the person
   pages to render the card for a record whose programme is not French any more,
   or was created by an admin who never saw the form. */
export function hasFrenchAnswers(answers) {
  const view = answers || {};
  return FRENCH_FIELDS.some((field) => String(view[field] || '').trim());
}

export function wantsFrenchPlacementTest(answers) {
  return String((answers || {}).frenchPlacementPreference || '') === 'placement_test';
}

export function claimsFrenchTestResult(answers) {
  return String((answers || {}).frenchTestProof || '') === 'yes';
}

export default {
  FRENCH_GOAL_OPTIONS,
  FRENCH_TARGET_LEVEL_OPTIONS,
  FRENCH_CURRENT_LEVEL_OPTIONS,
  FRENCH_TEST_PROOF_OPTIONS,
  FRENCH_PLACEMENT_OPTIONS,
  FRENCH_FIELDS,
  frenchLabel,
  frenchQuestionsAsked,
  hasFrenchAnswers,
};
